import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import RichTextEditor from '../../components/admin/RichTextEditor';
import ImageUpload from '../../components/admin/ImageUpload';
import { useBlog } from '../../contexts/BlogContext';
import { useAuth } from '../../contexts/AuthContext';
import { blogService } from '../../services/blogService';
import { 
  Save, 
  Tag,
  FolderOpen,
  Clock,
  FileText,
  CheckCircle,
  AlertCircle
} from 'lucide-react';

const BlogEditor = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { createPost, updatePost, categories, loading: categoriesLoading } = useBlog();
  const { user } = useAuth();
  
  const isEditMode = !!id;
  const [currentPost, setCurrentPost] = useState(null);
  const [loading, setLoading] = useState(false);

  const [formData, setFormData] = useState({
    title: '',
    slug: '',
    excerpt: '',
    content: '',
    category: 'Web Development',
    tags: '',
    image: '',
    metaTitle: '',
    metaDescription: '',
    keywords: '',
    author: user?.name || 'Admin',
    status: 'draft',
    readTime: 5
  });

  const [errors, setErrors] = useState({});
  const [saveStatus, setSaveStatus] = useState('');

  // Fetch fresh post data from API when in edit mode
  useEffect(() => {
    const fetchPost = async () => {
      if (isEditMode && id) {
        try {
          setLoading(true);
          console.log('🔄 Fetching fresh post data from API for ID:', id);
          const post = await blogService.getPostById(parseInt(id));
          console.log('✅ Fresh post data loaded:', post);
          setCurrentPost(post);
          
          // Convert storage path to full URL for image preview
          let imageUrl = post.featured_image || post.image || '';
          if (imageUrl && !imageUrl.startsWith('http') && !imageUrl.startsWith('data:')) {
            // It's a storage path, convert to full URL
            imageUrl = `http://localhost/karnidigitalsolutions/backend/public/storage/${imageUrl}`;
          }

          setFormData({
            title: post.title,
            slug: post.slug,
            excerpt: post.excerpt,
            content: post.content,
            // category can be object from DB or string from legacy
            category: post.category?.name || post.category || 'Web Development',
            tags: Array.isArray(post.tags) ? post.tags.join(', ') : typeof post.tags === 'string' ? post.tags : '',
            image: imageUrl,
            metaTitle: post.metaTitle || post.title,
            metaDescription: post.metaDescription || post.excerpt || '',
            keywords: post.keywords || '',
            author: post.author?.name || post.author || 'Admin',
            status: post.status || (post.published ? 'published' : 'draft'),
            readTime: post.readTime ?? 5
          });
        } catch (error) {
          console.error('❌ Error fetching post:', error);
          setSaveStatus('error');
        } finally {
          setLoading(false);
        }
      }
    };
    
    fetchPost();
  }, [isEditMode, id]);

  // Loading guard to avoid reading properties of undefined during initial fetch
  if (loading || categoriesLoading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  const generateSlug = (title) => {
    return title
      .toLowerCase()
      .replace(/[^\w\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/--+/g, '-')
      .trim();
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
      ...(name === 'title' && !isEditMode ? { slug: generateSlug(value) } : {})
    }));
    
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.title.trim()) newErrors.title = 'Title is required';
    if (!formData.slug.trim()) newErrors.slug = 'Slug is required';
    if (!formData.excerpt.trim()) newErrors.excerpt = 'Excerpt is required';
    if (!formData.content.trim()) newErrors.content = 'Content is required';
    if (!formData.metaTitle.trim()) newErrors.metaTitle = 'Meta title is required for SEO';
    if (!formData.metaDescription.trim()) newErrors.metaDescription = 'Meta description is required for SEO';
    
    if (formData.metaTitle.length > 60) newErrors.metaTitle = 'Meta title should be under 60 characters';
    if (formData.metaDescription.length > 160) newErrors.metaDescription = 'Meta description should be under 160 characters';
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (status) => {
    if (!validateForm()) {
      setSaveStatus('error');
      return;
    }

    // Resolve category_id from selected category label
    const selectedCat = Array.isArray(categories)
      ? categories.find(c => c.name === formData.category || c.slug === formData.category)
      : null;
    const category_id = selectedCat?.id || null;

    // Set published status based on button clicked or current status
    const published = (status || formData.status) === 'published';
    
    // Create FormData for multipart upload (supports both text fields and file uploads)
    const payload = new FormData();
    
    // Add all form fields to FormData
    payload.append('title', formData.title);
    payload.append('slug', formData.slug);
    payload.append('excerpt', formData.excerpt);
    payload.append('content', formData.content);
    payload.append('category_id', category_id);
    payload.append('published', published ? '1' : '0');
    
    // Add meta fields
    payload.append('metaTitle', formData.metaTitle);
    payload.append('metaDescription', formData.metaDescription);
    payload.append('keywords', formData.keywords);
    payload.append('readTime', formData.readTime);
    payload.append('author', formData.author);
    
    // Handle tags
    if (formData.tags) {
      payload.append('tags', formData.tags);
    }
    
    // Handle featured image - could be a File object or URL string
    if (formData.image) {
      console.log('📸 Processing image:', formData.image.substring(0, 50));
      // If it's a base64 data URL from ImageUpload component
      if (formData.image.startsWith('data:image')) {
        // Convert base64 to blob for upload
        try {
          const response = await fetch(formData.image);
          const blob = await response.blob();
          payload.append('featured_image', blob, 'image.jpg');
          console.log('✅ Base64 converted to blob:', blob.size, 'bytes');
        } catch (error) {
          console.error('Error converting base64 to blob:', error);
          payload.append('featured_image', formData.image);
        }
      } else {
        // It's a URL string, pass as is
        payload.append('featured_image', formData.image);
        console.log('✅ Image URL added to payload');
      }
    }

    // Debug: Log FormData contents
    console.log('📦 FormData payload contents:');
    for (let [key, value] of payload.entries()) {
      if (key === 'featured_image' && value instanceof Blob) {
        console.log(`  ${key}: [Blob ${value.size} bytes]`);
      } else {
        console.log(`  ${key}: ${typeof value === 'string' ? value.substring(0, 50) : value}`);
      }
    }

    try {
      setSaveStatus('saving');
      
      if (isEditMode) {
        const updatedPost = await updatePost(parseInt(id), payload);
        console.log('✅ Post updated successfully:', updatedPost);
        setSaveStatus('updated');
        
        // Fetch fresh data from API instead of reloading page
        setTimeout(async () => {
          try {
            const freshPost = await blogService.getPostById(parseInt(id));
            console.log('✅ Fresh post data after update:', freshPost);
            setCurrentPost(freshPost);
            
            // Convert storage path to full URL for image preview
            let imageUrl = freshPost.featured_image || freshPost.image || '';
            if (imageUrl && !imageUrl.startsWith('http') && !imageUrl.startsWith('data:')) {
              imageUrl = `http://localhost/karnidigitalsolutions/backend/public/storage/${imageUrl}`;
            }

            setFormData({
              title: freshPost.title,
              slug: freshPost.slug,
              excerpt: freshPost.excerpt,
              content: freshPost.content,
              category: freshPost.category?.name || freshPost.category || 'Web Development',
              tags: freshPost.tags?.join(', ') || '',
              image: imageUrl,
              metaTitle: freshPost.metaTitle || freshPost.title,
              metaDescription: freshPost.metaDescription || freshPost.excerpt || '',
              keywords: freshPost.keywords || '',
              author: freshPost.author?.name || freshPost.author || 'Admin',
              status: freshPost.status || (freshPost.published ? 'published' : 'draft'),
              readTime: freshPost.readTime ?? 5
            });
            
            setSaveStatus('');
          } catch (error) {
            console.error('❌ Error fetching fresh data:', error);
          }
        }, 1000);
      } else {
        await createPost(payload);
        setSaveStatus('created');
        
        setTimeout(() => {
          navigate('/admin/dashboard');
        }, 1200);
      }
    } catch (error) {
      console.error('Submit error:', error);
      setSaveStatus('error');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white shadow-md sticky top-16 z-40 border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-primary">
                {isEditMode ? 'Edit Blog Post' : 'Create New Blog Post'}
              </h1>
              <p className="text-sm text-gray-500">
                {isEditMode ? 'Update your existing post' : 'Write and publish a new blog post'}
              </p>
            </div>
            
            <div className="flex items-center gap-3">
              {saveStatus === 'saving' && (
                <span className="text-blue-600 font-semibold flex items-center gap-2">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
                  Saving...
                </span>
              )}
              {saveStatus === 'created' && (
                <span className="text-green-600 font-semibold flex items-center gap-2">
                  <CheckCircle className="w-5 h-5" />
                  Post Created!
                </span>
              )}
              {saveStatus === 'updated' && (
                <span className="text-green-600 font-semibold flex items-center gap-2">
                  <CheckCircle className="w-5 h-5" />
                  Post Updated Successfully!
                </span>
              )}
              {saveStatus === 'error' && (
                <span className="text-red-600 font-semibold flex items-center gap-2">
                  <AlertCircle className="w-5 h-5" />
                  Please fix errors
                </span>
              )}
              
              <button
                onClick={() => handleSubmit('draft')}
                className="bg-gray-600 hover:bg-gray-700 text-white px-6 py-2 rounded-lg flex items-center gap-2 transition-colors font-semibold"
              >
                <Save className="w-5 h-5" />
                Save Draft
              </button>
              
              <button
                onClick={() => handleSubmit('published')}
                className="bg-primary hover:bg-primary-700 text-white px-6 py-2 rounded-lg flex items-center gap-2 transition-colors font-semibold"
              >
                <CheckCircle className="w-5 h-5" />
                Publish
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Title */}
            <div className="bg-white rounded-xl shadow-md p-6">
              <label className="block text-sm font-bold text-gray-700 mb-2">
                Post Title *
              </label>
              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleChange}
                className={`w-full px-4 py-3 border-2 rounded-lg text-lg font-semibold focus:outline-none transition-colors ${
                  errors.title ? 'border-red-500' : 'border-gray-200 focus:border-primary'
                }`}
                placeholder="Enter an engaging title..."
              />
              {errors.title && <p className="text-red-500 text-sm mt-1">{errors.title}</p>}
              
              <div className="mt-4">
                <label className="block text-sm font-bold text-gray-700 mb-2">
                  URL Slug *
                </label>
                <input
                  type="text"
                  name="slug"
                  value={formData.slug}
                  onChange={handleChange}
                  className={`w-full px-4 py-2 border-2 rounded-lg focus:outline-none transition-colors ${
                    errors.slug ? 'border-red-500' : 'border-gray-200 focus:border-primary'
                  }`}
                  placeholder="post-url-slug"
                />
                {errors.slug && <p className="text-red-500 text-sm mt-1">{errors.slug}</p>}
                <p className="text-xs text-gray-500 mt-1">
                  URL: /blog/{formData.slug || 'post-slug'}
                </p>
              </div>
            </div>

            {/* Excerpt */}
            <div className="bg-white rounded-xl shadow-md p-6">
              <label className="block text-sm font-bold text-gray-700 mb-2">
                Excerpt (Short Description) *
              </label>
              <textarea
                name="excerpt"
                value={formData.excerpt}
                onChange={handleChange}
                rows="3"
                className={`w-full px-4 py-3 border-2 rounded-lg focus:outline-none transition-colors ${
                  errors.excerpt ? 'border-red-500' : 'border-gray-200 focus:border-primary'
                }`}
                placeholder="Write a compelling summary (1-2 sentences)..."
              />
              {errors.excerpt && <p className="text-red-500 text-sm mt-1">{errors.excerpt}</p>}
              <p className="text-xs text-gray-500 mt-1">
                {formData.excerpt.length} characters
              </p>
            </div>

            {/* Content Editor - WYSIWYG */}
            <div className="bg-white rounded-xl shadow-md p-6">
              <div className="flex items-center justify-between mb-4">
                <label className="block text-sm font-bold text-gray-700 flex items-center gap-2">
                  <FileText className="w-4 h-4" />
                  Content (WordPress-Style Editor) *
                </label>
                <span className="text-xs text-green-600 font-semibold">
                  ✨ Professional WYSIWYG Editor
                </span>
              </div>
              <RichTextEditor
                value={formData.content}
                onChange={(content) => {
                  setFormData(prev => ({ ...prev, content }));
                  if (errors.content) {
                    setErrors(prev => ({ ...prev, content: '' }));
                  }
                }}
                placeholder="Start writing your amazing content here..."
              />
              {errors.content && <p className="text-red-500 text-sm mt-2">{errors.content}</p>}
              
              {/* Editor Features */}
              <div className="mt-4 p-4 bg-green-50 rounded-lg border border-green-200">
                <p className="text-xs text-green-800 font-semibold mb-2">🎨 Editor Features:</p>
                <div className="grid grid-cols-2 gap-2 text-xs text-green-700">
                  <div>✅ Bold, Italic, Underline</div>
                  <div>✅ Headings (H2, H3)</div>
                  <div>✅ Bullet & Numbered Lists</div>
                  <div>✅ Text Alignment</div>
                  <div>✅ Insert Links & Images</div>
                  <div>✅ Live Preview Mode</div>
                </div>
              </div>
            </div>

            {/* SEO Section */}
            <div className="bg-white rounded-xl shadow-md p-6">
              <h3 className="text-lg font-bold text-primary mb-4 flex items-center gap-2">
                <FileText className="w-5 h-5" />
                SEO Settings (Important for Google!)
              </h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2">
                    Meta Title (For Google Search Results) *
                  </label>
                  <input
                    type="text"
                    name="metaTitle"
                    value={formData.metaTitle}
                    onChange={handleChange}
                    maxLength="60"
                    className={`w-full px-4 py-2 border-2 rounded-lg focus:outline-none transition-colors ${
                      errors.metaTitle ? 'border-red-500' : 'border-gray-200 focus:border-primary'
                    }`}
                    placeholder="SEO-optimized title (50-60 characters)"
                  />
                  {errors.metaTitle && <p className="text-red-500 text-sm mt-1">{errors.metaTitle}</p>}
                  <p className={`text-xs mt-1 ${formData.metaTitle.length > 60 ? 'text-red-500' : 'text-gray-500'}`}>
                    {formData.metaTitle.length}/60 characters
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2">
                    Meta Description (For Google Search Results) *
                  </label>
                  <textarea
                    name="metaDescription"
                    value={formData.metaDescription}
                    onChange={handleChange}
                    rows="3"
                    maxLength="160"
                    className={`w-full px-4 py-2 border-2 rounded-lg focus:outline-none transition-colors ${
                      errors.metaDescription ? 'border-red-500' : 'border-gray-200 focus:border-primary'
                    }`}
                    placeholder="Compelling description for search results (150-160 characters)"
                  />
                  {errors.metaDescription && <p className="text-red-500 text-sm mt-1">{errors.metaDescription}</p>}
                  <p className={`text-xs mt-1 ${formData.metaDescription.length > 160 ? 'text-red-500' : 'text-gray-500'}`}>
                    {formData.metaDescription.length}/160 characters
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2">
                    Keywords (Comma Separated)
                  </label>
                  <input
                    type="text"
                    name="keywords"
                    value={formData.keywords}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-primary focus:outline-none transition-colors"
                    placeholder="web development Pattaya, website cost Thailand, SEO tips"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Target keywords for SEO (e.g., "Pattaya website", "web development Thailand")
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Featured Image - Drag & Drop Upload */}
            <div className="bg-white rounded-xl shadow-md p-6">
              <ImageUpload
                value={formData.image}
                onChange={(imageValue) => {
                  setFormData(prev => ({ ...prev, image: imageValue }));
                  // Clear any errors
                  if (errors.image) {
                    setErrors(prev => ({ ...prev, image: '' }));
                  }
                }}
                label="Featured Image (Drag & Drop)"
              />
            </div>

            {/* Category */}
            <div className="bg-white rounded-xl shadow-md p-6">
              <label className="block text-sm font-bold text-gray-700 mb-2 flex items-center gap-2">
                <FolderOpen className="w-4 h-4" />
                Category
              </label>
              <select
                name="category"
                value={formData.category}
                onChange={handleChange}
                className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-primary focus:outline-none transition-colors"
              >
                {Array.isArray(categories) && categories.length > 0 ? (
                  categories.map(cat => (
                    <option key={cat.id} value={cat.name || cat.slug}>{cat.name || cat.slug}</option>
                  ))
                ) : (
                  <option value="Web Development">Web Development</option>
                )}
              </select>
            </div>

            {/* Tags */}
            <div className="bg-white rounded-xl shadow-md p-6">
              <label className="block text-sm font-bold text-gray-700 mb-2 flex items-center gap-2">
                <Tag className="w-4 h-4" />
                Tags
              </label>
              <input
                type="text"
                name="tags"
                value={formData.tags}
                onChange={handleChange}
                className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-primary focus:outline-none transition-colors"
                placeholder="web development, SEO, Pattaya"
              />
              <p className="text-xs text-gray-500 mt-1">
                Separate tags with commas
              </p>
            </div>

            {/* Read Time */}
            <div className="bg-white rounded-xl shadow-md p-6">
              <label className="block text-sm font-bold text-gray-700 mb-2 flex items-center gap-2">
                <Clock className="w-4 h-4" />
                Read Time (Minutes)
              </label>
              <input
                type="number"
                name="readTime"
                value={formData.readTime}
                onChange={handleChange}
                min="1"
                max="60"
                className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-primary focus:outline-none transition-colors"
              />
            </div>

            {/* Author */}
            <div className="bg-white rounded-xl shadow-md p-6">
              <label className="block text-sm font-bold text-gray-700 mb-2">
                Author
              </label>
              <input
                type="text"
                name="author"
                value={formData.author}
                onChange={handleChange}
                className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-primary focus:outline-none transition-colors"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BlogEditor;
